import { Text, View } from 'react-native';
import type { SpotTypeLegendItem } from '../RoomLayoutMap/spotTypeLegend';
import {
  hexWithAlpha,
  isValidHexColor,
  normalizeHex,
  SELECTED_SPOT_FILL_ALPHA_HEX
} from '../../colors/hexColor';
import { styles } from './SpotLegend.styles';

export type SpotLegendSpotVariant = 'outline-light' | 'outline-dark' | 'filled' | 'non-bookable';

export type SpotLegendSpotItem = {
  key: string;
  label: string;
  variant: SpotLegendSpotVariant;
};

/** Alias for SpotTypeLegendItem — same shape, single source of truth in spotTypeLegend.ts */
export type SpotLegendSpotTypeItem = SpotTypeLegendItem;

export const DEFAULT_SPOT_ITEMS: SpotLegendSpotItem[] = [
  { key: 'booked', label: 'Booked spot', variant: 'outline-light' },
  { key: 'available', label: 'Available spot', variant: 'outline-dark' },
  { key: 'selected', label: 'Selected spot', variant: 'filled' }
];

/** Status legend without Selected — used for read-only preview (`selectionDisabled`). */
export const PREVIEW_SPOT_ITEMS: SpotLegendSpotItem[] = DEFAULT_SPOT_ITEMS.filter(
  (item) => item.key !== 'selected'
);

const DEFAULT_SPOT_TYPE_ITEMS: SpotTypeLegendItem[] = [
  { tag: 'RF', label: 'Rowformer' },
  { tag: 'BF', label: 'Bikeformer' }
];

export type SpotLegendProps = {
  spotItems?: SpotLegendSpotItem[];
  /** Omit or pass undefined to use DEFAULT_SPOT_TYPE_ITEMS. */
  spotTypeItems?: SpotTypeLegendItem[];
  /** Optional: hex color for the "Your spot" (filled) legend square. When set, overrides default purple. */
  selectedSpotColor?: string;
};

function getSquareStyle(variant: SpotLegendSpotVariant, selectedSpotColor?: string) {
  switch (variant) {
    case 'outline-light':
      return [styles.square, styles.squareOutlineLight];
    case 'outline-dark':
      return [styles.square, styles.squareOutlineDark];
    case 'non-bookable':
      return [styles.square, styles.squareNonBookable];
    case 'filled': {
      if (isValidHexColor(selectedSpotColor)) {
        const hex = normalizeHex(selectedSpotColor);
        return [
          styles.square,
          styles.squareFilled,
          {
            backgroundColor: hexWithAlpha(hex, SELECTED_SPOT_FILL_ALPHA_HEX),
            borderColor: hex,
            borderWidth: 2
          }
        ];
      }
      return [styles.square, styles.squareFilled];
    }
  }
}

export default function SpotLegend({
  spotItems = DEFAULT_SPOT_ITEMS,
  spotTypeItems = DEFAULT_SPOT_TYPE_ITEMS,
  selectedSpotColor
}: SpotLegendProps) {
  return (
    <View style={styles.card} testID="spot-legend">
      <View style={styles.row} testID="spot-legend-spots">
        {spotItems.map(({ key, label, variant }) => (
          <View key={key} style={styles.spotItem} testID={`spot-legend-${key}`}>
            <View style={styles.spotIconColumn}>
              <View
                style={getSquareStyle(variant, selectedSpotColor)}
                testID={`spot-legend-square-${key}`}
              />
            </View>
            <Text style={styles.label}>{label}</Text>
          </View>
        ))}
      </View>
      <View style={styles.rowSpacer} />
      <View style={styles.row} testID="spot-legend-spot-types">
        {spotTypeItems.map(({ tag, label }) => {
          const displayTag = tag.trim();
          return (
            <View
              key={displayTag}
              style={styles.machineItem}
              testID={`spot-legend-spot-type-${displayTag}`}
            >
              <View style={styles.machineTagColumn}>
                <Text style={styles.machineTag} numberOfLines={1}>
                  {displayTag}
                </Text>
              </View>
              <Text style={styles.machineLabel} numberOfLines={1}>
                {label}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
